import { useFeatureFlags, useUpdateFeatureFlag } from "@portal/api/feature-flags";
import { useMemo } from "react";
import { Checkbox } from "../ui/Checkbox";
import { ErrorDisplay } from "../ui/ErrorDisplay";
import { MutationErrorFlag } from "../ui/MutationErrorFlag";
import { Spinner } from "../ui/Spinner";

const formatFlagName = (name: string) =>
	name.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

export const ManageFeatureFlags = () => {
	const { featureFlags, featureFlagsLoading, featureFlagsError } = useFeatureFlags();
	const { updateFeatureFlag, updateFeatureFlagError, resetUpdateFeatureFlagError } =
		useUpdateFeatureFlag();

	const sortedFeatureFlags = useMemo(
		() => featureFlags && [...featureFlags].sort((a, b) => a.name.localeCompare(b.name)),
		[featureFlags],
	);

	return (
		<div
			data-testid="feature-flags-container"
			className="flex flex-col justify-center items-start w-full border rounded-lg"
		>
			{featureFlagsLoading && (
				<div className="flex p-4 w-full justify-center items-center">
					<Spinner />
				</div>
			)}
			{featureFlagsError && (
				<ErrorDisplay
					error={featureFlagsError}
					componentToDisplay={
						<div className="flex w-full p-4 text-sm justify-center items-center">
							<span>Unable to load feature flags.</span>
							<button
								type="button"
								className="cursor-pointer m-1 text-link hover:text-link-hover"
								onClick={() => window.location.reload()}
							>
								Retry
							</button>
						</div>
					}
				/>
			)}
			{sortedFeatureFlags && !featureFlagsLoading && !featureFlagsError && (
				<div className="flex flex-col w-full divide-y">
					{sortedFeatureFlags.length === 0 ? (
						<div className="flex w-full p-4 text-sm justify-center items-center">
							<span>No feature flags.</span>
						</div>
					) : (
						sortedFeatureFlags.map((flag) => (
							<div key={flag.name} className="flex items-center gap-3 px-4 py-3">
								<Checkbox
									checked={flag.enabled}
									onChange={() => updateFeatureFlag({ name: flag.name, enabled: !flag.enabled })}
								/>
								<div className="flex flex-col">
									<span className="text-sm font-medium">{formatFlagName(flag.name)}</span>
									<span className="text-xs text-secondary">{flag.description}</span>
								</div>
							</div>
						))
					)}
				</div>
			)}
			<MutationErrorFlag
				errorHeading="Update Feature Flag Error"
				mutationError={updateFeatureFlagError}
				dismissError={resetUpdateFeatureFlagError}
			/>
		</div>
	);
};
